import React, { createRef, FC, useMemo, useRef } from 'react';
import { useTranslation, Trans } from '@wix/wix-i18n-config';
import { useInspirationalTemplates } from '../../hooks/useInspirationalTemplates';
import { InspirationalTemplate } from '../InspirationalTemplate/InspirationalTemplate';
import { LinkWithQuery } from '../common/LinkWithQuery/LinkWithQuery';
import { INSPIRATIONAL_CATEGORY_SLUG } from '../../config';

import s from './TemplatesCarousel.scss';
import { useBiLogger } from '../../contexts/BILoggerContext';

const GENERAL_ACTION = 'browse_inspirational_templates';
import { useCarouselScroll } from './hooks/useCarouselScroll';
import { usePageScroll } from '../../contexts/PageScrollContext';
import { useInternalRoutes } from '../../hooks/useInternalRoutes';

export const TemplatesCarousel: FC = () => {
  const inspirationalTemplates = useInspirationalTemplates();
  const { t } = useTranslation();
  const biLogger = useBiLogger();
  const { scrollToTop } = usePageScroll();
  const { category } = useInternalRoutes();

  const handleSeeAllClick = () => {
    scrollToTop();
    biLogger.reportGeneralAction({
      action: GENERAL_ACTION,
      actionValue: 'inspirational_templates',
      categorySlug: INSPIRATIONAL_CATEGORY_SLUG,
    });
  };

  const templatesRefs = useMemo(
    () => inspirationalTemplates.map(() => createRef<HTMLDivElement>()),
    [inspirationalTemplates],
  );

  const carouselRef = useRef<HTMLDivElement>(null);

  const {
    showNextTemplate,
    showPreviousTemplate,
    isStartPosition,
    isEndPosition,
  } = useCarouselScroll({
    templatesRefs,
    carouselRef,
  });

  const handleCarouselArrowClick =
    (direction: 'backward' | 'forward') => () => {
      biLogger.reportGeneralAction({
        action: GENERAL_ACTION,
        actionValue: direction,
        categorySlug: INSPIRATIONAL_CATEGORY_SLUG,
      });
      direction === 'forward' ? showNextTemplate() : showPreviousTemplate();
    };

  return (
    <div className={s.container}>
      <div className={s.textBlock}>
        <div className={s.title}>{t('app.templates.carousel.title')}</div>
        <div className={s.description}>
          <Trans
            i18nKey="app.templates.carousel.description"
            components={{
              Link: (
                <LinkWithQuery
                  to={category.build(INSPIRATIONAL_CATEGORY_SLUG)}
                  className={s.seeAllLink}
                  onClick={handleSeeAllClick}
                />
              ),
            }}
          />
        </div>
      </div>
      <div className={s.carousel} ref={carouselRef}>
        {inspirationalTemplates.map((template, index) => (
          <InspirationalTemplate
            className={s.template}
            ref={templatesRefs[index]}
            key={template.templateId}
            template={template}
            backgroundColor={template.backgroundColor}
            index={index}
            withTag
          />
        ))}
      </div>
      <div className={s.arrowsContainer}>
        <button
          className={s.arrow}
          disabled={isStartPosition}
          onClick={handleCarouselArrowClick('backward')}
        >
          ←
        </button>
        <button
          className={s.arrow}
          disabled={isEndPosition}
          onClick={handleCarouselArrowClick('forward')}
        >
          →
        </button>
      </div>
    </div>
  );
};
